import { useQuery, useMutation, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query';
import { auditLogService } from '@repo/ui/lib/api/services/audit-log.service';
import {
  AuditLog,
  AuditLogFilters,
  AuditLogStats,
  PaginatedAuditLogs,
} from '@repo/ui/lib/api/types/audit-log.types';

/**
 * Query keys for audit logs
 */
export const auditLogKeys = {
  all: ['audit-logs'] as const,
  lists: () => [...auditLogKeys.all, 'list'] as const,
  list: (filters: AuditLogFilters) => [...auditLogKeys.lists(), filters] as const,
  details: () => [...auditLogKeys.all, 'detail'] as const,
  detail: (id: string) => [...auditLogKeys.details(), id] as const,
  stats: (startDate?: string, endDate?: string) =>
    [...auditLogKeys.all, 'stats', startDate, endDate] as const,
  related: (id: string, context: number) =>
    [...auditLogKeys.all, 'related', id, context] as const,
};

/**
 * Hook to fetch paginated audit logs with filters
 * @param filters - Filter parameters
 * @param options - React Query options
 * @returns Query result with audit logs
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = useAuditLogs({
 *   page: 1,
 *   limit: 50,
 *   severity: ['ERROR', 'CRITICAL']
 * });
 * ```
 */
export function useAuditLogs(
  filters: AuditLogFilters = {},
  options?: Omit<UseQueryOptions<PaginatedAuditLogs>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: auditLogKeys.list(filters),
    queryFn: () => auditLogService.getAuditLogs(filters),
    staleTime: 30000, // 30 seconds
    ...options,
  });
}

/**
 * Hook to fetch a single audit log by ID
 * @param id - Audit log ID
 * @param options - React Query options
 * @returns Query result with audit log
 * 
 * @example
 * ```tsx
 * const { data: log } = useAuditLog('log-id-123');
 * ```
 */
export function useAuditLog(
  id: string,
  options?: Omit<UseQueryOptions<AuditLog>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: auditLogKeys.detail(id),
    queryFn: () => auditLogService.getAuditLogById(id),
    enabled: !!id,
    staleTime: 60000, // 1 minute
    ...options,
  });
}

/**
 * Hook to fetch audit log statistics
 * @param startDate - Start date filter (ISO string)
 * @param endDate - End date filter (ISO string)
 * @param options - React Query options
 * @returns Query result with statistics
 * 
 * @example
 * ```tsx
 * const { data: stats } = useAuditStats(
 *   '2024-01-01',
 *   '2024-12-31'
 * );
 * ```
 */
export function useAuditStats(
  startDate?: string,
  endDate?: string,
  options?: Omit<UseQueryOptions<AuditLogStats>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: auditLogKeys.stats(startDate, endDate),
    queryFn: () => auditLogService.getAuditStats(startDate, endDate),
    staleTime: 60000, // 1 minute
    ...options,
  });
}

/**
 * Hook to fetch related audit logs (context)
 * @param logId - Audit log ID
 * @param context - Number of logs to fetch before/after
 * @param options - React Query options
 * @returns Query result with related logs
 * 
 * @example
 * ```tsx
 * const { data: relatedLogs } = useRelatedLogs('log-id', 5);
 * ```
 */
export function useRelatedLogs(
  logId: string,
  context: number = 5,
  options?: Omit<UseQueryOptions<AuditLog[]>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: auditLogKeys.related(logId, context),
    queryFn: () => auditLogService.getRelatedLogs(logId, context),
    enabled: !!logId,
    staleTime: 60000, // 1 minute
    ...options,
  });
}

/**
 * Hook to export audit logs
 * @param options - Mutation options
 * @returns Mutation object for exporting logs
 * 
 * @example
 * ```tsx
 * const exportMutation = useExportAuditLogs();
 * 
 * const handleExport = () => {
 *   exportMutation.mutate({
 *     filters: { severity: ['ERROR'] },
 *     format: 'csv'
 *   });
 * };
 * ```
 */
export function useExportAuditLogs(
  options?: UseMutationOptions<
    Blob,
    Error,
    { filters: AuditLogFilters; format: 'csv' | 'json' }
  >
) {
  return useMutation({
    mutationFn: async ({ filters, format }) => {
      return auditLogService.exportAuditLogs(filters, format);
    },
    onSuccess: (blob, variables) => {
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `audit-logs-${new Date().toISOString()}.${variables.format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    ...options,
  });
}

/**
 * Hook to get Prometheus metrics
 * @param options - React Query options
 * @returns Query result with metrics
 */
export function useAuditMetrics(
  options?: Omit<UseQueryOptions<string>, 'queryKey' | 'queryFn'>
) {
  return useQuery({
    queryKey: [...auditLogKeys.all, 'metrics'],
    queryFn: () => auditLogService.getMetrics(),
    staleTime: 30000, // 30 seconds
    ...options,
  });
}
